import { useMemo, useState } from 'react';
import { LuPencil, LuTrash } from 'react-icons/lu';

import { TaskTemplate } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { stripHtmlToText, titleCase } from 'utils';
import { useAuthContext } from 'views/auth/Auth';

import { useListTemplatesByAdvisor } from '../task-templates-queries';
import CreateUpdateTemplateDialog from './CreateUpdateTemplateDialog';
import DeleteTemplateDialog from './DeleteTemplateDialog';

interface TemplateDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSelectTemplate: (template: TaskTemplate) => void;
}

export const TemplateDialog = ({ isOpen, onClose, onSelectTemplate }: TemplateDialogProps) => {
    const { user } = useAuthContext();
    const { data: templates = [] } = useListTemplatesByAdvisor(user?.personOrgGUID as string);
    const [searchQuery, setSearchQuery] = useState('');
    const [editOpen, setEditOpen] = useState(false);
    const [editing, setEditing] = useState<TaskTemplate | null>(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deletingTemplate, setDeletingTemplate] = useState<TaskTemplate | null>(null);

    const filteredTemplates = useMemo(() => {
        const q = searchQuery.toLowerCase();
        return templates.filter(
            (t) =>
                (t.title || '').toLowerCase().includes(q) ||
                stripHtmlToText(t.description || '')
                    .toLowerCase()
                    .includes(q) ||
                titleCase(t.type).toLowerCase().includes(q),
        );
    }, [templates, searchQuery]);

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[700px]">
                <DialogHeader>
                    <DialogTitle>Task Templates</DialogTitle>
                </DialogHeader>

                <div className="py-4">
                    <div className="mb-4 flex items-center justify-between">
                        <Input
                            placeholder="Search templates..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="max-w-sm"
                        />
                        <Button
                            onClick={() => {
                                setEditing(null);
                                setEditOpen(true);
                            }}
                        >
                            New Template
                        </Button>
                    </div>

                    <ScrollArea className="h-[400px]">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Title</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead className="w-24 text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredTemplates.length > 0 ? (
                                    filteredTemplates.map((template) => (
                                        <TableRow key={template.taskTemplateGUID} className="hover:bg-muted/50">
                                            <TableCell className="font-medium">{template.title || ''}</TableCell>
                                            <TableCell className="capitalize">
                                                {titleCase(template.type).toLowerCase()}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end space-x-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => {
                                                            setEditing(template);
                                                            setEditOpen(true);
                                                        }}
                                                    >
                                                        <LuPencil className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => {
                                                            setDeletingTemplate(template);
                                                            setDeleteOpen(true);
                                                        }}
                                                    >
                                                        <LuTrash className="text-destructive h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => onSelectTemplate(template)}
                                                    >
                                                        Use
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={3} className="text-muted-foreground py-6 text-center">
                                            {searchQuery ? 'No matching templates found' : 'No templates available'}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </ScrollArea>
                </div>
                {deletingTemplate && (
                    <DeleteTemplateDialog
                        template={deletingTemplate}
                        open={deleteOpen}
                        onOpenChange={(open) => {
                            setDeleteOpen(open);
                            if (!open) setDeletingTemplate(null);
                        }}
                        onCancel={() => {
                            setDeleteOpen(false);
                            setDeletingTemplate(null);
                        }}
                    />
                )}
                <CreateUpdateTemplateDialog
                    open={editOpen}
                    onOpenChange={(open) => {
                        setEditOpen(open);
                        if (!open) setEditing(null);
                    }}
                    template={editing}
                />
            </DialogContent>
        </Dialog>
    );
};

export default TemplateDialog;
